import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { askMoo } from '../../../queries/ask-moo';
import * as api from '../api/moo-tasks.api';
import type { MooTaskCreate, MooTaskUpdate } from '../types/moo-tasks.types';

/** Options accepted by the two tenant-wide task hooks below.
 *
 *  `enabled` MUST be applied at the `useQuery` call site, not passed into the
 *  `askMoo.*` factory: @lukemorales/query-key-factory silently drops every
 *  factory-entry property except queryKey/queryFn/contextQueries, so an
 *  `enabled` set inside the factory is dead code. */
interface TaskQueryOptions {
  readonly enabled?: boolean;
}

/** Returns the TENANT's tasks, not the caller's — the backend applies no owner
 *  filter. Callers outside the `account:cowbell` Tasks routes must pass
 *  `{ enabled: false }` when the user lacks that scope, so other people's task
 *  records never cross the network to a browser that may not see them.
 *  Filtering after the payload arrives is a display fix, not a privacy one. */
export const useMooTasksList = (
  params?: Record<string, unknown>,
  options?: TaskQueryOptions
) =>
  useQuery({ ...askMoo.mooTasks(params), enabled: options?.enabled ?? true });

export const useMooTask = (taskId: string) =>
  useQuery({ ...askMoo.mooTask(taskId), enabled: !!taskId });

export const useTaskRuns = (taskId: string, params?: Record<string, unknown>) =>
  // keepPreviousData: while a new page (changed offset) is fetching, retain the
  // prior page's data — including `total` — so paginated callers don't see a
  // transient empty/total=0 state that would reset the page or flash an empty
  // table between pages.
  useQuery({
    ...askMoo.mooTaskRuns(taskId, params),
    enabled: !!taskId,
    keepPreviousData: true,
  });

/** Tenant-wide run history — same privacy caveat as `useMooTasksList` above:
 *  `GET /task-runs` has no owner filter, so gate the fetch with `enabled`
 *  rather than relying on a client-side filter. */
export const useAllTaskRuns = (
  params?: Record<string, unknown>,
  options?: TaskQueryOptions
) =>
  // keepPreviousData for the same reason as useTaskRuns above: paginated
  // callers must not see a transient empty/total=0 state between pages.
  useQuery({
    ...askMoo.allTaskRuns(params),
    keepPreviousData: true,
    enabled: options?.enabled ?? true,
  });

export const useTriggerCatalog = () => useQuery(askMoo.triggerCatalog());

export const useOutputCatalog = () => useQuery(askMoo.outputCatalog());

const useInvalidateTasks = () => {
  const qc = useQueryClient();
  // Invalidate both the list and any per-task detail cache so the detail
  // view's status badge refreshes after activate/stop/update/delete.
  return () => {
    qc.invalidateQueries({ queryKey: askMoo.mooTasks._def });
    qc.invalidateQueries({ queryKey: askMoo.mooTask._def });
  };
};

export const useCreateTask = () => {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: (body: MooTaskCreate) =>
      api.createMooTask({ data: body }).then((r) => r.data),
    onSuccess: invalidate,
  });
};

export const useUpdateTask = () => {
  const invalidate = useInvalidateTasks();
  // Take the id at mutate time so callers never bind to a stale id.
  return useMutation({
    mutationFn: ({ taskId, body }: { taskId: string; body: MooTaskUpdate }) =>
      api.updateMooTask(taskId, { data: body }).then((r) => r.data),
    onSuccess: invalidate,
  });
};

export const useDeleteTask = () => {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: (taskId: string) =>
      api.deleteMooTask(taskId).then((r) => r.data),
    onSuccess: invalidate,
  });
};

export const useActivateTask = () => {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: (taskId: string) =>
      api.activateMooTask(taskId).then((r) => r.data),
    onSuccess: invalidate,
  });
};

export const useStopTask = () => {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: (taskId: string) => api.stopMooTask(taskId).then((r) => r.data),
    onSuccess: invalidate,
  });
};

/**
 * Resume this task's proactive nudges for the current user (OMNI-757). Muting
 * happens from the nudge card; this is the way back out, so the task list and
 * detail must re-read their `muted` flag afterwards — hence the invalidate.
 */
export const useUnmuteTask = () => {
  const invalidate = useInvalidateTasks();
  return useMutation({
    mutationFn: (taskId: string) =>
      api.unmuteMooTask(taskId).then((r) => r.data),
    onSuccess: invalidate,
  });
};

// Invalidate run-history queries AND the task caches so Run now / re-run /
// wizard test refresh the run lists, the Activity view, and the task table's
// last_run_at / task detail.
const useInvalidateRuns = () => {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: askMoo.mooTaskRuns._def });
    qc.invalidateQueries({ queryKey: askMoo.allTaskRuns._def });
    qc.invalidateQueries({ queryKey: askMoo.mooTasks._def });
    qc.invalidateQueries({ queryKey: askMoo.mooTask._def });
  };
};

export const useRunTask = () => {
  const invalidate = useInvalidateRuns();
  return useMutation({
    mutationFn: ({
      taskId,
      rerunFromRunId,
    }: {
      taskId: string;
      rerunFromRunId?: string;
    }) =>
      (rerunFromRunId
        ? api.runMooTask(taskId, {
            data: { rerun_from_run_id: rerunFromRunId },
          })
        : api.runMooTask(taskId)
      ).then((r) => r.data),
    onSuccess: invalidate,
  });
};

/** Start a test run. Returns {run_id} immediately (202); the run executes in the background. */
export const useTestTask = () =>
  useMutation({
    mutationFn: ({
      taskId,
      deliverOutputs,
      entityType,
      entityId,
    }: {
      taskId: string;
      deliverOutputs: boolean;
      entityType?: string;
      entityId?: string;
    }) =>
      api
        .testMooTask(taskId, {
          data: {
            deliver_outputs: deliverOutputs,
            entity_type: entityType,
            entity_id: entityId,
          },
        })
        .then((r) => r.data),
  });

/** Poll a single run until it leaves 'running', then stop. */
export const useTestTaskRun = (taskId: string, runId: string | null) => {
  const invalidate = useInvalidateRuns();
  return useQuery({
    queryKey: ['askMoo', 'testRun', taskId, runId],
    queryFn: () => api.getTaskRun(taskId, runId!).then((r) => r.data),
    enabled: !!runId,
    refetchInterval: (run, query) => {
      if (query.state.status === 'error') return false;
      if (!run || run.status === 'running') return 1000;
      invalidate();
      return false;
    },
  });
};

/** Retry a run's failed background units: spawns a new task run
 *  (triggered_by 'retry') with only the failed units preloaded. */
export const useRetryTaskRunFailedUnits = () => {
  const invalidate = useInvalidateRuns();
  return useMutation({
    mutationFn: ({ taskId, runId }: { taskId: string; runId: string }) =>
      api.retryTaskRunFailedUnits(taskId, runId).then((r) => r.data),
    onSuccess: invalidate,
  });
};

/** Cancel a run parked on background children — stops every child shard
 *  group; the run finalizes failed ("cancelled by user"), no delivery. */
export const useCancelTaskRun = () => {
  const invalidate = useInvalidateRuns();
  return useMutation({
    mutationFn: ({ taskId, runId }: { taskId: string; runId: string }) =>
      api.cancelTaskRun(taskId, runId).then((r) => r.data),
    onSuccess: invalidate,
  });
};

/** Per-child unit/progress detail for a run parked on background async
 *  children. Polls every 5s while the run is background_running; a run
 *  that has already finished is fetched once (for the final unit
 *  breakdown) and never repolled. */
export const useTaskRunBackgroundProgress = (
  taskId: string,
  runId: string,
  runStatus: string,
  // Callers that only sometimes need the breakdown (e.g. the run drawer,
  // where a run without background children has nothing to fetch) pass
  // false to skip the request entirely.
  enabled = true
) =>
  useQuery({
    ...askMoo.taskRunBackgroundProgress(taskId, runId),
    enabled: enabled && !!taskId && !!runId,
    refetchInterval: (data, query) => {
      if (query.state.status === 'error') return false;
      const status = data?.run_status ?? runStatus;
      return status === 'background_running' ? 5000 : false;
    },
  });
